import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useTeamNameDisplayStore } from "@/state/useTeamNameDisplayStore";
import { useLocaleFormat } from "@/hooks/useLocaleFormat";
import { holderName, matchTeamsLabel } from "@/lib/media/scanLogic";
import { getLocalizedStadiumName } from "@/utils/teamNameDisplay";
import { formatLocationLabel } from "@/utils/locationDisplay";
import { ALGIERS_TZ } from "@/utils/algiersTime";
import type { DisplayMode } from "@/utils/nameHelpers";
import type { LocationLabelSource } from "@/utils/locationDisplay";

type OrgLike = { name: string; name_ar?: string | null } | null | undefined;
type PersonLike =
    | {
          first_name?: string | null;
          last_name?: string | null;
          first_name_ar?: string | null;
          last_name_ar?: string | null;
          profile?: { full_name?: string | null } | null;
      }
    | null
    | undefined;
type MatchLike =
    | {
          match_date?: string | null;
          match_time?: string | null;
          home_team?: { name: string; name_ar?: string | null } | null;
          away_team?: { name: string; name_ar?: string | null } | null;
          stadium?: {
              name: string;
              name_ar?: string | null;
              location?: LocationLabelSource | null;
          } | null;
      }
    | null
    | undefined;

/** Organisation name in ONE language (displayMode), Latin fallback. */
export function localizedOrgName(org: OrgLike, displayMode: DisplayMode): string | null {
    if (!org) return null;
    const ar = org.name_ar?.trim();
    if (displayMode === "arabic" && ar) return ar;
    return org.name?.trim() || ar || null;
}

/**
 * Entity labels shared by the « Aperçu » and « Matchs » tabs: one language per
 * name (displayMode), Algiers wall time for every date and time.
 */
export function useMediaCockpitLabels() {
    const { t } = useTranslation("media");
    const displayMode = useTeamNameDisplayStore((s) => s.displayMode);
    const { formatDate, formatNumber, locale } = useLocaleFormat();

    const person = useCallback(
        (p: PersonLike) =>
            holderName(p, p?.profile ?? null, displayMode) ?? t("shared.unknown_name"),
        [displayMode, t]
    );

    const org = useCallback(
        (o: OrgLike) => localizedOrgName(o, displayMode) ?? t("shared.unknown_organization"),
        [displayMode, t]
    );

    const teams = useCallback(
        (m: MatchLike) => matchTeamsLabel(m ?? null, displayMode, t("demands.vs")) ?? "—",
        [displayMode, t]
    );

    /** « sam. 3 oct. » — a DATE column, read at Algiers noon so no zone can shift it. */
    const matchDay = useCallback(
        (m: MatchLike, options?: Intl.DateTimeFormatOptions) => {
            const date = m?.match_date?.slice(0, 10);
            if (!date) return "—";
            return formatDate(`${date}T12:00:00+01:00`, {
                weekday: "short",
                day: "numeric",
                month: "short",
                timeZone: ALGIERS_TZ,
                ...options,
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [locale]
    );

    /** « 15:00 » (TIME column, Algiers wall time) or null. */
    const kickoffTime = useCallback((m: MatchLike) => {
        const time = m?.match_time?.trim();
        return time && time.length >= 4 ? time.slice(0, 5) : null;
    }, []);

    /** Hour:minute of an instant in Algiers time. */
    const clock = useCallback(
        (iso: string) =>
            formatDate(iso, { hour: "2-digit", minute: "2-digit", timeZone: ALGIERS_TZ }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [locale]
    );

    /** Day + time of an instant in Algiers time. */
    const dateTime = useCallback(
        (iso: string) =>
            formatDate(iso, {
                day: "numeric",
                month: "short",
                hour: "2-digit",
                minute: "2-digit",
                timeZone: ALGIERS_TZ,
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [locale]
    );

    const stadium = useCallback(
        (m: MatchLike) => {
            const s = m?.stadium;
            if (!s) return null;
            return [getLocalizedStadiumName(s, displayMode), formatLocationLabel(s.location, displayMode)]
                .filter(Boolean)
                .join(" — ");
        },
        [displayMode]
    );

    return useMemo(
        () => ({
            displayMode,
            person,
            org,
            teams,
            matchDay,
            kickoffTime,
            clock,
            dateTime,
            stadium,
            formatNumber,
        }),
        [displayMode, person, org, teams, matchDay, kickoffTime, clock, dateTime, stadium, formatNumber]
    );
}
